/**
 * Small rich text editor (contenteditable + execCommand) for product descriptions, blog posts and richtext fields.
 * Value is HTML; the server sanitizes it again (render/sanitize.go richtext / article policy).
 * mode="article" adds headings, images, code and HTML source; mode="richtext" is the component field subset.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Divider, Input, Modal, Space, Tooltip } from 'antd';
import {
    BoldOutlined, ItalicOutlined, UnderlineOutlined, StrikethroughOutlined, OrderedListOutlined, UnorderedListOutlined,
    LinkOutlined, ClearOutlined, CodeOutlined, PictureOutlined, DisconnectOutlined,
} from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { MediaPickerModal } from './Media';
import { LinkTargetInput } from './editor/DataPicker';

const ALLOWED_RICH = new Set(['P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'A', 'UL', 'OL', 'LI', 'H3', 'H4', 'BLOCKQUOTE', 'CODE', 'MARK', 'SUB', 'SUP', 'SPAN']);
const ALLOWED_ARTICLE = new Set([...ALLOWED_RICH, 'H2', 'H5', 'HR', 'PRE', 'IMG', 'FIGURE', 'FIGCAPTION', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD', 'CAPTION', 'DIV']);
const SAFE_URL = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

/** client-side clean-up of pasted/edited HTML (DOMParser; the server has the final say) */
export const cleanHTML = (html, article = false) => {
    const allowed = article ? ALLOWED_ARTICLE : ALLOWED_RICH;
    const doc = new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html');
    const walk = (node) => {
        [...node.childNodes].forEach((ch) => {
            if (ch.nodeType === 8) { ch.remove(); return; }
            if (ch.nodeType !== 1) return;
            if (['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'META', 'LINK'].includes(ch.tagName)) { ch.remove(); return; }
            walk(ch);
            if (!allowed.has(ch.tagName)) {
                // unwrap unknown tags (DIV → P in richtext)
                if (ch.tagName === 'DIV' && !article) {
                    const p = doc.createElement('p');
                    while (ch.firstChild) p.appendChild(ch.firstChild);
                    ch.replaceWith(p);
                } else {
                    ch.replaceWith(...ch.childNodes);
                }
                return;
            }
            [...ch.attributes].forEach((a) => {
                const n = a.name.toLowerCase();
                const keep = (n === 'href' && ch.tagName === 'A' && SAFE_URL.test(a.value))
                    || (n === 'target' && ch.tagName === 'A')
                    || (article && ch.tagName === 'IMG' && ['src', 'alt', 'width', 'height'].includes(n) && (n !== 'src' || /^(https:|\/)/i.test(a.value)))
                    || (article && ['colspan', 'rowspan'].includes(n));
                if (!keep) ch.removeAttribute(a.name);
            });
        });
    };
    walk(doc.body);
    return doc.body.innerHTML.replace(/<p><br><\/p>$/, '');
};

/** link dialog: a site page / product / post … or a URL (the editor's link picker), optional new tab */
const LinkModal = ({ open, siteId, initial, onClose, onOk }) => {
    const { t } = useTranslation();
    const [v, setV] = useState(initial);
    useEffect(() => { if (open) setV(initial); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
    const ok = !!v?.href && SAFE_URL.test(v.href);
    return (
        <Modal open={open} title={t('Link')} onCancel={onClose} onOk={() => ok && onOk(v)} okButtonProps={{ disabled: !ok }} width={420} destroyOnHidden zIndex={1050}>
            <LinkTargetInput siteId={siteId} value={v} onChange={setV} />
        </Modal>
    );
};

/** image dialog (article mode): media library or an https URL, alt text */
const ImageModal = ({ open, siteId, onClose, onOk }) => {
    const { t } = useTranslation();
    const [v, setV] = useState({ src: '', alt: '' });
    const [picking, setPicking] = useState(false);
    useEffect(() => { if (open) setV({ src: '', alt: '' }); }, [open]);
    const ok = /^(https:|\/)/i.test(v.src);
    return (
        <Modal open={open} title={t('Image', { context: 'shop' })} onCancel={onClose} onOk={() => ok && onOk(v)} okButtonProps={{ disabled: !ok }} width={460} destroyOnHidden zIndex={1050}>
            {v.src && ok && <img src={v.src} alt="" style={{ maxWidth: '100%', maxHeight: 160, display: 'block', margin: '0 auto 8px', borderRadius: 4 }} />}
            <Space.Compact style={{ width: '100%' }}>
                <Input placeholder="https://…" value={v.src} onChange={(e) => setV({ ...v, src: e.target.value })} />
                {siteId && <Tooltip title={t('Media library')}><Button icon={<PictureOutlined />} onClick={() => setPicking(true)} /></Tooltip>}
            </Space.Compact>
            <Input placeholder={t('Alt text')} value={v.alt} onChange={(e) => setV({ ...v, alt: e.target.value })} style={{ marginTop: 8 }} />
            {siteId && <MediaPickerModal open={picking} siteId={siteId} onClose={() => setPicking(false)} onPick={(m) => setV((x) => ({ src: m.url, alt: x.alt || m.alt || '' }))} />}
        </Modal>
    );
};

const RichEditor = ({ value, onChange, mode = 'article', minHeight = 180, placeholder, siteId: siteIdProp }) => {
    const { t } = useTranslation();
    const params = useParams();
    const siteId = siteIdProp || params?.id || null; // every caller lives under /sites/:id/…
    const ref = useRef(null);
    const range = useRef(null); // selection when a dialog opened
    const [dialog, setDialog] = useState(null); // {kind: 'link', initial, anchor} | {kind: 'image'}
    const last = useRef(value || '');
    const [source, setSource] = useState(false);
    const article = mode === 'article';

    useEffect(() => {
        if (ref.current && !source && (value || '') !== last.current) {
            ref.current.innerHTML = value || '';
            last.current = value || '';
        }
    }, [value, source]);
    useEffect(() => { if (ref.current) ref.current.innerHTML = value || ''; }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

    const emit = () => {
        const html = cleanHTML(ref.current?.innerHTML || '', article);
        last.current = html;
        onChange?.(html);
    };
    const exec = (cmd, arg) => {
        ref.current?.focus();
        document.execCommand(cmd, false, arg);
        emit();
    };
    const saveRange = () => {
        const sel = window.getSelection();
        const r = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
        range.current = r && ref.current?.contains(r.commonAncestorContainer) ? r.cloneRange() : null;
        return range.current;
    };
    const restoreRange = () => {
        ref.current?.focus();
        const sel = window.getSelection();
        if (range.current && sel) { sel.removeAllRanges(); sel.addRange(range.current); }
        return range.current;
    };
    const link = () => {
        const r = saveRange();
        let n = r?.commonAncestorContainer || null;
        while (n && n !== ref.current && n.nodeName !== 'A') n = n.parentNode;
        const anchor = n && n.nodeName === 'A' ? n : null;
        setDialog({
            kind: 'link', anchor,
            initial: anchor ? { href: anchor.getAttribute('href') || '', label: anchor.textContent, target: anchor.getAttribute('target') || '' } : { href: '', label: r ? String(r) : '', target: '' },
        });
    };
    const applyLink = (v) => {
        const d = dialog;
        setDialog(null);
        let a = d?.anchor && ref.current?.contains(d.anchor) ? d.anchor : null;
        if (!a) {
            const r = restoreRange();
            if (!r) return;
            a = document.createElement('a');
            if (r.collapsed) a.textContent = v.label || v.href;
            else a.appendChild(r.extractContents());
            r.insertNode(a);
            const sel = window.getSelection();
            sel?.removeAllRanges();
            const after = document.createRange();
            after.setStartAfter(a);
            sel?.addRange(after);
        } else if (v.label && v.label !== a.textContent && !a.children.length) a.textContent = v.label;
        a.setAttribute('href', v.href);
        if (v.target === '_blank') a.setAttribute('target', '_blank'); else a.removeAttribute('target');
        emit();
    };
    const image = () => { saveRange(); setDialog({ kind: 'image' }); };
    const applyImage = (v) => {
        setDialog(null);
        const r = restoreRange();
        const img = document.createElement('img');
        img.setAttribute('src', v.src);
        img.setAttribute('alt', v.alt || '');
        if (r) { r.deleteContents(); r.insertNode(img); } else ref.current?.appendChild(img);
        emit();
    };
    const onPaste = (e) => {
        const html = e.clipboardData?.getData('text/html');
        if (html) {
            e.preventDefault();
            document.execCommand('insertHTML', false, cleanHTML(html, article));
            emit();
        }
    };
    const B = ({ icon, title, onClick, children }) => (
        <Tooltip title={title} mouseEnterDelay={0.4}>
            <Button size="small" type="text" icon={icon} onMouseDown={(e) => e.preventDefault()} onClick={onClick} disabled={source}>{children}</Button>
        </Tooltip>
    );

    return (
        <div className="yc-rich" style={{ border: '1px solid var(--ant-color-border, #d9d9d9)', borderRadius: 6, background: 'var(--ant-color-bg-container, #fff)' }}>
            <Space size={0} wrap style={{ padding: 4, borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)', width: '100%' }}>
                <B icon={<BoldOutlined />} title={t('Bold')} onClick={() => exec('bold')} />
                <B icon={<ItalicOutlined />} title={t('Italic')} onClick={() => exec('italic')} />
                <B icon={<UnderlineOutlined />} title={t('Underline')} onClick={() => exec('underline')} />
                <B icon={<StrikethroughOutlined />} title={t('Strikethrough')} onClick={() => exec('strikeThrough')} />
                <Divider type="vertical" />
                {article && <B title={t('Heading')} onClick={() => exec('formatBlock', 'H2')}>H2</B>}
                <B title={t('Heading')} onClick={() => exec('formatBlock', 'H3')}>H3</B>
                <B title={t('Paragraph')} onClick={() => exec('formatBlock', 'P')}>¶</B>
                <B title={t('Quote')} onClick={() => exec('formatBlock', 'BLOCKQUOTE')}>❝</B>
                <B icon={<UnorderedListOutlined />} title={t('Bulleted list')} onClick={() => exec('insertUnorderedList')} />
                <B icon={<OrderedListOutlined />} title={t('Numbered list')} onClick={() => exec('insertOrderedList')} />
                <Divider type="vertical" />
                <B icon={<LinkOutlined />} title={t('Link')} onClick={link} />
                <B icon={<DisconnectOutlined />} title={t('Remove link')} onClick={() => exec('unlink')} />
                {article && <B icon={<PictureOutlined />} title={t('Image', { context: 'shop' })} onClick={image} />}
                <B icon={<ClearOutlined />} title={t('Clear formatting')} onClick={() => exec('removeFormat')} />
                {article && (
                    <Tooltip title={t('HTML')}>
                        <Button size="small" type={source ? 'primary' : 'text'} icon={<CodeOutlined />} onClick={() => setSource((s) => !s)} />
                    </Tooltip>
                )}
            </Space>
            {source ? (
                <Input.TextArea value={value || ''} onChange={(e) => { last.current = e.target.value; onChange?.(e.target.value); }}
                    autoSize={{ minRows: 8 }} style={{ fontFamily: 'monospace', border: 0, borderRadius: 0 }}
                    onBlur={() => onChange?.(cleanHTML(value || '', article))} />
            ) : (
                <div
                    ref={ref}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={placeholder || ''}
                    onInput={emit}
                    onBlur={emit}
                    onPaste={onPaste}
                    style={{ minHeight, padding: '8px 12px', outline: 'none', overflowWrap: 'anywhere', lineHeight: 1.6 }}
                />
            )}
            <LinkModal open={dialog?.kind === 'link'} siteId={siteId} initial={dialog?.initial} onClose={() => setDialog(null)} onOk={applyLink} />
            {article && <ImageModal open={dialog?.kind === 'image'} siteId={siteId} onClose={() => setDialog(null)} onOk={applyImage} />}
        </div>
    );
};

export default RichEditor;
